/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { Link, useParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import { InputGroup } from 'react-bootstrap'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ColumnSelector from "../../components/ColumnSelector.jsx"
import ConfirmationModal from '../../components/ConfirmationModal.jsx'
import CopyRowAsJSON from '../../components/CopyRowAsJSON.jsx'
import DebugAccordion from '../../components/DebugAccordion.jsx'
import DeviceKeysModal from './components/DeviceKeysModal.jsx'
import DevicesListFilteringModal from './components/DevicesListFilteringModal.jsx'
import DownloadProgress from '../../components/DownloadProgress.jsx'
import Icon from '../../components/Icon.jsx'
import IncompatibleDevicesModal from './components/IncompatibleDevicesModal.jsx'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import PageSizeSelector from '../../components/PageSizeSelector.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import PushFormsModal from './components/PushFormsModal.jsx'
import TableColumnHeader from '../../components/TableColumnHeader.jsx'
import TimestampWithUTCTooltip from '../../components/TimestampWithUTCTooltip.jsx'

import { fetchDeviceList, exportDeviceList } from '../../state/actions/deviceActions.js'
import { fetchDeviceForms } from '../../state/actions/deviceFormActions'
import { addNotification, NotificationTypes } from '../../state/actions/notificationActions.js'
import { updatePageSize, updateDeviceListFilter, removeDeviceListFilter, updateDeviceColumnConfig } from '../../state/actions/userSettingsActions.js'

import { businessEntityDictionarySignal } from '../../state/signals/businessEntitySignals.js'
import { deviceListSignal, deviceListLoadedSignal } from '../../state/signals/deviceSignals.js'
import { deviceFormsSignal } from '../../state/signals/deviceFormSignals.js'
import { pageSizeSignal, debugSignal, deviceListFiltersValueSignal, deviceListFiltersCountSignal, selectedBusinessEntityIdSignal, deviceColumnConfigSignal } from '../../state/signals/userSettingsSignals.js'

import { TaskType, SortDirectionType } from '../../utils/constants.js'
import { computeDisplayData, computeSortedData, computeStartDisplayRowNumber, computeEndDisplayRowNumber, computeTotalPages } from '../../utils/table.js'
import { saveFile } from '../../utils/file.js'

function TableRow({ item, selectedRowsSignal, activeColumnsComputed, canEditComputed, handleSelectRow, showPushFormsModal, renderDeviceCell }) {
	const hasDeviceKeys = item.deviceStatusDeviceKey0 != null
		|| item.deviceStatusDeviceKey1 != null
		|| item.deviceStatusDeviceKey2 != null
		|| item.deviceStatusDeviceKey3 != null
		|| item.deviceStatusDeviceKey4 != null
		|| item.deviceStatusDeviceKey5 != null
	return (
		<tr>
			<td>
				<Form.Check hidden={item.deviceStatusDeviceType == null} aria-label="Select Row" checked={selectedRowsSignal.value[''.concat(item.deviceConfigId)]} onChange={() => handleSelectRow(item)} />
			</td>
			<td>
				<Dropdown>
					<Dropdown.Toggle variant="link" size="sm" id="actions">
						<Icon icon="gear" width={16} height={16}></Icon>
					</Dropdown.Toggle>
					<Dropdown.Menu>
						<CopyRowAsJSON item={item} field='componentNickname' />
						{canEditComputed.value && <Dropdown.Item hidden={item.deviceStatusDeviceType == null} onClick={() => showPushFormsModal(item, TaskType.UpdateDeviceForms)}><Icon icon="arrow-circle-o-up" width="16" height="16"></Icon>&nbsp;Push forms</Dropdown.Item>}
						{canEditComputed.value && <Dropdown.Item hidden={item.deviceStatusDeviceType == null} onClick={() => showPushFormsModal(item, TaskType.UpdateDeviceFirmware)}><Icon icon="refresh" width="16" height="16"></Icon>&nbsp;Update Firmware</Dropdown.Item>}
					</Dropdown.Menu>
				</Dropdown>
			</td>
			{activeColumnsComputed.value.map(col => (
				<td key={col.key}>{renderDeviceCell(col.key, item, hasDeviceKeys)}</td>
			))}
		</tr>
	)
}

function TableRows({ items, deviceListLoadedSignal, activeColumnsComputed, selectedRowsSignal, canEditComputed, handleSelectRow, showPushFormsModal, renderDeviceCell }) {
	const colSpan = activeColumnsComputed.value.length + 2
	if (!deviceListLoadedSignal.value) {
		return <tr><td colSpan={colSpan}><LoadingAnimation /></td></tr>
	}
	if (items && items.length) {
		let keys = new Set()
		return items.map(item => {
			let key = item.deviceConfigId
			if (keys.has(key)) {
				return
			}
			keys.add(key)
			return (
				<TableRow
					item={item}
					key={`${key}`}
					selectedRowsSignal={selectedRowsSignal}
					activeColumnsComputed={activeColumnsComputed}
					canEditComputed={canEditComputed}
					handleSelectRow={handleSelectRow}
					showPushFormsModal={showPushFormsModal}
					renderDeviceCell={renderDeviceCell}
				/>
			)
		})
	}
	else if (items) {
		return (
			<tr>
				<td colSpan={colSpan} className="text-center">No devices were found for the currently selected business entity.</td>
			</tr>
		)
	}
}

const DEVICE_COLUMN_DEFS = [
	{ key: 'merchant', title: 'Merchant', datafield: 'componentMerchant', sortable: true, defaultVisible: true },
	{ key: 'nickname', title: 'UTG Nickname', datafield: 'componentNickname', sortable: true, defaultVisible: true },
	{ key: 'deviceSerial', title: 'Device Serial', datafield: 'deviceStatusDeviceSerialNumber', sortable: true, defaultVisible: true },
	{ key: 'deviceConfigured', title: 'Device Configured', datafield: 'deviceConfigDeviceType', sortable: true, defaultVisible: true },
	{ key: 'deviceReported', title: 'Device Reported', datafield: 'deviceStatusDeviceType', sortable: true, defaultVisible: true },
	{ key: 'terminalId', title: 'Terminal ID', datafield: 'deviceConfigApiTerminalId', sortable: true, defaultVisible: true },
	{ key: 'pciVersion', title: 'PCI Version', datafield: 'deviceStatusDevicePciVersion', sortable: true, defaultVisible: true },
	{ key: 'firmwareVersion', title: 'Firmware Version', datafield: 'deviceStatusFirmwareVersion', sortable: true, defaultVisible: true },
	{ key: 'deviceKeys', title: 'Device Keys', datafield: '', sortable: false, defaultVisible: true },
	{ key: 'machineDnsName', title: 'Machine DNS Name', datafield: 'machineDnsName', sortable: true, defaultVisible: true },
	{ key: 'utgStatus', title: 'UTG Status', datafield: 'utgStatus', sortable: true, defaultVisible: true },
	{ key: 'utgVersion', title: 'UTG Version', datafield: 'utgVersion', sortable: true, defaultVisible: true },
	{ key: 'agentVersion', title: 'Agent Version', datafield: 'agentVersion', sortable: true, defaultVisible: true },
	{ key: 'configStatus', title: 'Configuration Status', datafield: 'configStatus', sortable: true, defaultVisible: true },
	{ key: 'profile', title: 'Profile', datafield: 'componentProfileName', sortable: true, defaultVisible: true },
	{ key: 'profileVersion', title: 'Profile Version', datafield: 'componentProfileVersion', sortable: true, defaultVisible: true },
	{ key: 'location', title: 'Location/Serial', datafield: 'componentLocation', sortable: true, defaultVisible: true },
	{ key: 'lane', title: 'Lane', datafield: 'componentLane', sortable: true, defaultVisible: true },
	{ key: 'fileId', title: 'File ID', datafield: 'componentFileId', sortable: true, defaultVisible: true },
	{ key: 'machineStatus', title: 'Machine Status', datafield: 'machineStatus', sortable: true, defaultVisible: true },
	{ key: 'lastKnownComm', title: 'Last Known Communication', datafield: 'machineStatusEntityLastKnownCommunication', sortable: true, defaultVisible: true },
	{ key: 'nextPredictedComm', title: 'Next Predicted Communication', datafield: 'machineStatusEntityNextPredictedCommunication', sortable: true, defaultVisible: true },
	{ key: 'machineNickname', title: 'Machine Nickname', datafield: 'machineNickname', sortable: true, defaultVisible: true },
	{ key: 'parentAccount', title: 'Parent Account', datafield: 'businessEntityName', sortable: true, defaultVisible: true },
	{ key: 'timeZone', title: 'Time Zone', datafield: 'machineStatusEntityStandardTimeZone', sortable: true, defaultVisible: true },
	{ key: 'windowsVersion', title: 'Windows Version', datafield: 'machineStatusEntityWindowsVersion', sortable: true, defaultVisible: true },
	{ key: 'deviceMode', title: 'Device Mode', datafield: 'deviceConfigDeviceMode', sortable: true, defaultVisible: true },
]

function Devices() {

	const navigate = useNavigate()
	const { beId } = useParams()
	const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

	useEffect(() => {
		if (!userPermissionsSummary.value || !userPermissionsSummary.value.canViewDevices) {
            addNotification('You are not authorized to view devices for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
		}
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditDevices
    })

	const currentPageSignal = useSignal(1)
	const devicePciVersionSignal = useSignal(null)
	const filterStringSignal = useSignal("")
	const isSelectAllSignal = useSignal(false)
	const selectedRecordSignal = useSignal(null)

	const initializeSelectedRows = function () {
		let dict = {}
		for (let i in deviceListSignal.value) {
			dict[''.concat(deviceListSignal.value[i].deviceConfigId)] = false
		}
		return dict
	}

	const selectedRowsSignal = useSignal(initializeSelectedRows())
	const selectedDevicesSignal = useSignal([])
	const showModalDeviceKeysSignal = useSignal(false)
	const showModalIncompatibleDevicesSignal = useSignal(false)
	const showModalPushFormsSignal = useSignal(false)

	const sortColumnSignal = useSignal('')
	const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)
	const includeChildAccountsSignal = useSignal(false)

	const taskTypeSignal = useSignal(0)
	const selectedRowsCountLabelSignal = useSignal(0)

	//Export Devices as csv
	const showDownloadProgressSignal = useSignal(false)
	const downloadProgressMessageSignal = useSignal('')
	const showExportConfirmationModalSignal = useSignal(false)

	useEffect(() => {
		if (selectedBusinessEntityIdSignal.value !== beId) {
			removeDeviceListFilter()
		}
		fetchDeviceList(beId, deviceListFiltersValueSignal.value, config, accessToken, includeChildAccountsSignal.value)
		fetchDeviceForms(beId, null, config, accessToken)
	}, [beId, config, accessToken])

	const deviceIncompatibilitySignal = useSignal(false)
	const pciIncompatibilitySignal = useSignal(false)
	const utgIncompatibilitySignal = useSignal(false)

	const filteredDataComputed = useComputed(() => {
		try {
			let filtered;
			if (!filterStringSignal.value) {
				filtered = deviceListSignal.value ? [...deviceListSignal.value] : []
			}
			else {
				// Filter the data based on the filter text
				// Convert the filter to lowercase
				const lowerCaseFilter = filterStringSignal.value.toLowerCase()
				// Filter the data based on the filter text
				filtered = deviceListSignal.value.filter(item => {
					// Limit search to these fields
					let searchData = {
						componentMerchant: item.componentMerchant,
						componentNickname: item.componentNickname,
						utgStatus: item.utgStatus,
						utgVersion: item.utgVersion,
						agentVersion: item.agentVersion,
						configStatus: item.configStatus,
						componentProfileName: item.componentProfileName,
						componentProfileVersion: item.componentProfileVersion,
						componentLocation: item.componentLocation,
						componentLane: item.componentLane,
						componentFileId: item.componentFileId,
						machineDnsName: item.machineDnsName,
						machineStatus: item.machineStatus,
						machineStatusEntityLastKnownCommunication: item.machineStatusEntityLastKnownCommunication,
						machineStatusEntityNextPredictedCommunication: item.machineStatusEntityNextPredictedCommunication,
						machineNickname: item.machineNickname,
						businessEntityId: item.businessEntityId,
						businessEntityName: businessEntityDictionarySignal.value[item.businessEntityId]?.name,
						standardTimeZone: item.machineStatusEntityStandardTimeZone,
						windowsVersion: item.machineStatusEntityWindowsVersion,

						componentPermanentId: item.componentPermanentId,
						machinePermanentId: item.machinePermanentId,

						deviceConfigApiTerminalId: item.deviceConfigApiTerminalId,
						deviceConfigDeviceName: item.deviceConfigDeviceName,
						deviceConfigDeviceType: item.deviceConfigDeviceType,
						deviceStatusDevicePciVersion: item.deviceStatusDevicePciVersion,
						deviceStatusDeviceSerialNumber: item.deviceStatusDeviceSerialNumber,
						deviceStatusRBAVersion: item.deviceStatusRBAVersion
					}
					return Object.values(searchData).some(value => {
						if (value && (typeof (value) == 'string')) {
							return value.toString().toLowerCase().includes(lowerCaseFilter)
						}
					})
				})
			}
			return filtered
		} catch (x) {
			console.trace(x.toString())
			window.alert(x.toString())
		}
	})

	const enrichedDataComputed = useComputed(() =>
		filteredDataComputed.value.map(item => ({
			...item,
			businessEntityName: businessEntityDictionarySignal.value[item.businessEntityId]?.name ?? String(item.businessEntityId),
		}))
	)

	const sortedDataComputed = useComputed(computeSortedData(enrichedDataComputed, sortColumnSignal, sortDirectionSignal))

	const totalPagesComputed = useComputed(computeTotalPages(filteredDataComputed))

	const displayDataComputed = useComputed(computeDisplayData(currentPageSignal, sortedDataComputed))

	const startDisplayRowNumberComputed = useComputed(computeStartDisplayRowNumber(currentPageSignal))
	const endDisplayRowNumberComputed = useComputed(computeEndDisplayRowNumber(currentPageSignal, filteredDataComputed))

	const accordionData = [
		// { name: 'Properties', data: { Count: devicesSignal.value.length, 'Filter Count': filteredDataSignal.value.length, 'Display Count': displayDataSignal.value.length, Filter: filterStringSignal.value, CurrentPage: currentPageSignal.value, RowsPerPage: rowsPerPageSignal, accessToken: accessToken } },
		{ name: 'Filtered Data', data: filteredDataComputed.value },
		{ name: 'Data', data: deviceListSignal.value },
		{ name: 'Display Data', data: displayDataComputed.value },
		{ name: 'Selected Rows', data: selectedRowsSignal.value },
		{ name: 'Access Token', data: accessToken },
		{ name: 'App Config', data: config }
	]

	const activeColumnsComputed = useComputed(() => {
		const config = deviceColumnConfigSignal.value
		if (!config) return DEVICE_COLUMN_DEFS.filter(c => c.defaultVisible)
		return config
			.filter(saved => saved.visible)
			.map(saved => DEVICE_COLUMN_DEFS.find(c => c.key === saved.key))
			.filter(Boolean)
	})

	const columnSelectorStateComputed = useComputed(() => {
		const config = deviceColumnConfigSignal.value
		if (!config) return DEVICE_COLUMN_DEFS.map(c => ({ key: c.key, title: c.title, visible: c.defaultVisible }))
		const savedKeys = new Set(config.map(c => c.key))
		return [
			...config.map(saved => {
				const def = DEVICE_COLUMN_DEFS.find(c => c.key === saved.key)
				return def ? { key: def.key, title: def.title, visible: saved.visible } : null
			}).filter(Boolean),
			...DEVICE_COLUMN_DEFS.filter(c => !savedKeys.has(c.key)).map(c => ({ key: c.key, title: c.title, visible: c.defaultVisible }))
		]
	})

	const handleColumnConfigChange = (cols) => updateDeviceColumnConfig(cols)
	const handleColumnConfigReset = () => updateDeviceColumnConfig(null)

	const handlePageSizeSelect = (v) => {
		currentPageSignal.value = 1
        updatePageSize(parseInt(v, 10))

		let count = 0
		let displaySet = new Set()
		for (let i in displayDataComputed.value) {
			let dcid = ''.concat(displayDataComputed.value[i].deviceConfigId)
			displaySet.add(dcid)
			if (!(dcid in selectedRowsSignal.value)) {
				selectedRowsSignal.value[dcid] = false
			}
		}
		for (let i in selectedRowsSignal.value) {
			if (selectedRowsSignal.value[i] && displaySet.has(i)) {
				selectedRowsSignal.value[i] = true
				count++;
			}
			else {
				selectedRowsSignal.value[i] = false
			}
		}
		selectedRowsCountLabelSignal.value = count
	}
	const handleFilterStringChange = (e) => {
		currentPageSignal.value = 1
		filterStringSignal.value = e.currentTarget.value
	}

	const showPushFormsModal = function (item, taskType) {
		try {
			if (!selectedRowsSignal.value[item.deviceConfigId]) {
				handleSelectRow(item)
			}
			selectedDevicesSignal.value = []
			for (let i in displayDataComputed.value) {
				let item = displayDataComputed.value[i]
				if (item.deviceStatusDeviceType != null) {
					let dcid = ''.concat(item.deviceConfigId)
					if (selectedRowsSignal.value[dcid]) {
						selectedDevicesSignal.value.push(item)
					}
				}
			}
			if (selectedDevicesSignal.value.length == 0) {
				window.alert("No devices selected")
				return
			}
			const deviceStatusDeviceTypes = selectedDevicesSignal.value.map(device => device.deviceStatusDeviceType)
			let uniqueDeviceTypes = deviceStatusDeviceTypes.filter((item, i, arr) => arr.indexOf(item) === i)
			if (uniqueDeviceTypes.length == 1) {
				if (''.concat(uniqueDeviceTypes[0]).length == 0) {
					deviceIncompatibilitySignal.value = true
				} else {
					deviceIncompatibilitySignal.value = false
				}
			} else {
				deviceIncompatibilitySignal.value = true
			}
			utgIncompatibilitySignal.value = selectedDevicesSignal.value.some(function (device) {
				const softwareVersion = parseInt(device.utgVersion.split(".")[3]);
				if (softwareVersion < 3018) {
					return true;
				}
			})
			const devicePciVersions = selectedDevicesSignal.value.map(record => record.deviceStatusDevicePciVersion)
			let uniquePciVersions = devicePciVersions.filter((item, i, ar) => ar.indexOf(item) === i);
			if (uniquePciVersions.length == 1) {
				pciIncompatibilitySignal.value = false
			} else {
				pciIncompatibilitySignal.value = true
			}
			if (deviceIncompatibilitySignal.value || utgIncompatibilitySignal.value || pciIncompatibilitySignal.value) {
				showIncompatibleDevicesModal()
			} else {
				taskTypeSignal.value = taskType
				devicePciVersionSignal.value = devicePciVersions[0]
				devicePciVersionSignal.value = item.deviceStatusDevicePciVersion
				showModalPushFormsSignal.value = true
			}
		}
		catch (x) {
			console.trace()
			console.error("showPushFormsModal exception: ".concat(x.toString()))
			window.alert("showPushFormsModal exception: ".concat(x.toString()))
		}
	}

	const showIncompatibleDevicesModal = function () {
		showModalIncompatibleDevicesSignal.value = true
	}

	// Devices Filtering Modal
	const showDevicesFilteringModalSignal = useSignal(false)
	const handleCancelDevicesFiltering = () => showDevicesFilteringModalSignal.value = false
	const handleApplyDevicesFilters = (predicates) => {
		updateDeviceListFilter(predicates)
		fetchDeviceList(beId, deviceListFiltersValueSignal.value, config, accessToken, includeChildAccountsSignal.value)
		showDevicesFilteringModalSignal.value = false
	}
	const handleClearFilter = () => {
		removeDeviceListFilter()
		fetchDeviceList(beId, deviceListFiltersValueSignal.value, config, accessToken, includeChildAccountsSignal.value)
	}

	const handleIncludeChildAccountsToggle = (e) => {
		const checked = e.target.checked
		includeChildAccountsSignal.value = checked
		fetchDeviceList(beId, deviceListFiltersValueSignal.value, config, accessToken, checked)
	}

	const handleSelectAll = () => {
		try {
			if (isSelectAllSignal.value) {
				// deselect all
				for (let i in selectedRowsSignal.value) {
					selectedRowsSignal.value[i] = false
				}
				selectedRowsCountLabelSignal.value = 0
				isSelectAllSignal.value = false
				return
			}
			// select all rows from filtered data
			isSelectAllSignal.value = true
			let count = 0
			for (let i in displayDataComputed.value) {
				let item = displayDataComputed.value[i]
				if (item.deviceStatusDeviceType != null) {
					selectedRowsSignal.value[''.concat(item.deviceConfigId)] = true
					count++
				}
			}
			selectedRowsCountLabelSignal.value = count //displayDataSignal.value.length
		}
		catch (x) {
			console.trace(x.toString())
			window.alert(x.toString())
		}
	}

	const handleSelectRow = (item) => {
		const dcid = ''.concat(item.deviceConfigId)
		selectedRowsSignal.value[dcid] = !(selectedRowsSignal.value[dcid])
		if (selectedRowsSignal.value[dcid]) {
			selectedRowsCountLabelSignal.value++
		}
		else {
			selectedRowsCountLabelSignal.value--
		}
	}

	const handleDeviceKeysClicked = function (item) {
		selectedRecordSignal.value = item
		showModalDeviceKeysSignal.value = true
	}

	function renderDeviceCell(key, item, hasDeviceKeys) {
		switch (key) {
			case 'merchant': return item.componentMerchant
			case 'nickname': return <Link to={`../../be/${item.businessEntityId}/utgs/${item.componentId}`}>{item.componentNickname}</Link>
			case 'deviceSerial': return item.deviceStatusDeviceSerialNumber
			case 'deviceConfigured': return item.deviceConfigDeviceType
			case 'deviceReported': return item.deviceStatusDeviceType
			case 'terminalId': return item.deviceConfigApiTerminalId
			case 'pciVersion': return item.deviceStatusDevicePciVersion
			case 'firmwareVersion': return item.deviceStatusFirmwareVersion
			case 'deviceKeys': return (
				<>
					<a hidden={!hasDeviceKeys} style={{ cursor: 'pointer' }} onClick={() => handleDeviceKeysClicked(item)}>...</a>
					<span hidden={hasDeviceKeys}>N/A</span>
				</>
			)
			case 'machineDnsName': return item.machineDnsName
			case 'utgStatus': return item.utgStatus
			case 'utgVersion': return item.utgVersion
			case 'agentVersion': return item.agentVersion
			case 'configStatus': return item.configStatus
			case 'profile': return item.componentProfileName
			case 'profileVersion': return item.componentProfileVersion
			case 'location': return item.componentLocation
			case 'lane': return item.componentLane
			case 'fileId': return item.componentFileId
			case 'machineStatus': return item.machineStatus
			case 'lastKnownComm': return <TimestampWithUTCTooltip>{item.machineStatusEntityLastKnownCommunication}</TimestampWithUTCTooltip>
			case 'nextPredictedComm': return <TimestampWithUTCTooltip>{item.machineStatusEntityNextPredictedCommunication}</TimestampWithUTCTooltip>
			case 'machineNickname': return item.machineNickname
			case 'parentAccount': return item.businessEntityName
			case 'timeZone': return item.machineStatusEntityStandardTimeZone
			case 'windowsVersion': return item.machineStatusEntityWindowsVersion
			case 'deviceMode': return item.deviceConfigDeviceMode
			default: return null
		}
	}

	const setCurrentPage = function (pageNum) {
		if (currentPageSignal.value != pageNum) {
			currentPageSignal.value = pageNum
			for (let i in selectedRowsSignal.value) {
				selectedRowsSignal.value[i] = false
			}
			selectedRowsCountLabelSignal.value = 0
			isSelectAllSignal.value = false
		}
	}

	function PaginationItems() {
		let items = []
		for (let i = 1; i <= totalPagesComputed.value; i++) {
			items.push(
				<Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => { setCurrentPage(i) }}>{i}</Pagination.Item>
			)
		}
		return items
	}

	const handleCloseModalDeviceKeys = () => {
		showModalDeviceKeysSignal.value = false
		selectedRecordSignal.value = null

	}

	const handleCloseModalIncompatibleDevices = () => {
		console.log("Hiding the incompatible devices modal")
		showModalIncompatibleDevicesSignal.value = false
	}

	const handleCloseModalPushForms = () => {
		console.log("handleCloseModalPushForms")
		showModalPushFormsSignal.value = false
	}

	const exportDevices = async () => {
		// set showDownloadProgress to true to show spinner
		showDownloadProgressSignal.value = true
		downloadProgressMessageSignal.value = 'Preparing to export device list'
		const columnConfig = deviceColumnConfigSignal.value
		const activeColumns = (columnConfig
			? columnConfig.filter(c => c.visible).map(c => c.key)
			: DEVICE_COLUMN_DEFS.filter(c => c.defaultVisible).map(c => c.key)
		).flatMap(k => k === 'deviceKeys' ? ['deviceKey0', 'deviceKey4'] : [k])
		exportDeviceList(beId, deviceListFiltersValueSignal.value, activeColumns, config, accessToken).then(
			(exportDeviceListResponse) => {
				saveFile(exportDeviceListResponse, "devices.csv")
				addNotification('Exporting device list. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
				showDownloadProgressSignal.value = false
			}
		).catch(
			(error) => {
				console.log(error)
				addNotification(`Unable to export device list. ${error}`, NotificationTypes.ERROR, true)
				showDownloadProgressSignal.value = false
			}
		)
	}

	try {
		return (
			<>
				<DownloadProgress showSignal={showDownloadProgressSignal} messageSignal={downloadProgressMessageSignal} />
				<ConfirmationModal show={showExportConfirmationModalSignal.value} handleCancel={() => showExportConfirmationModalSignal.value = false} handleConfirm={() => { showExportConfirmationModalSignal.value = false; exportDevices(); }} message={'Export may take a few minutes to download ALL devices on this tab. Are you sure you want to continue?'} title={"Export device list"} />
				<Breadcrumb className="px-3 pt-1 bg-light">
					<Breadcrumb.Item active>Device Management</Breadcrumb.Item>
				</Breadcrumb>
				<PageTitle title='Device Management' canEditSignal={canEditComputed} />
				<Nav className="justify-content-between px-3 py-1 my-2 bg-light">
					<Nav.Item className="me-2">
						<InputGroup>
							<Button size="sm" variant="outline-secondary" onClick={() => handleClearFilter()}><Icon icon='trash' width="16" height="16"></Icon></Button>
							<Button size="sm" variant="outline-secondary" onClick={() => showDevicesFilteringModalSignal.value = true}><Icon icon='funnel' width="16" height="16"></Icon>&nbsp;Filters ({deviceListFiltersCountSignal.value})</Button>
						</InputGroup>
					</Nav.Item>
					<Nav.Item className="me-2">
						<PageSizeSelector pageSizeSignal={pageSizeSignal} handlePageSizeSelect={handlePageSizeSelect} />
					</Nav.Item>
					<Nav.Item className="me-auto">
						<ColumnSelector
							columns={columnSelectorStateComputed.value}
							onChange={handleColumnConfigChange}
							onReset={handleColumnConfigReset}
						/>
					</Nav.Item>
					<Nav.Item className="me-2 d-flex align-items-center">
						<Form.Check
							type="switch"
							id="include-child-accounts-switch"
							label="Include Child Accounts"
							checked={includeChildAccountsSignal.value}
							onChange={handleIncludeChildAccountsToggle}
						/>
					</Nav.Item>
					<Nav.Item className="me-2">
						<Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" checked={isSelectAllSignal.value} onChange={handleFilterStringChange} />
					</Nav.Item>
					<Nav.Item>
						<Button size="sm" variant="outline-secondary" onClick={exportDevices}><Icon icon='download' width="16" height="16"></Icon>&nbsp;Download data as CSV</Button>
					</Nav.Item>
				</Nav>
				<Table responsive striped hover size="sm" className="cc-table">
					<thead>
						<tr>
							<th><Form.Check type="checkbox" id="select-all-switch" aria-label="Select All" label={selectedRowsCountLabelSignal.value} checked={isSelectAllSignal.value} onChange={() => handleSelectAll()} /></th>
							<TableColumnHeader title={'Action'} sortable={false} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
							{activeColumnsComputed.value.map(col => (
								<TableColumnHeader key={col.key} title={col.title} sortable={col.sortable} datafield={col.datafield} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
							))}
						</tr>
					</thead>
					<tbody><TableRows
						items={displayDataComputed.value}
						deviceListLoadedSignal={deviceListLoadedSignal}
						activeColumnsComputed={activeColumnsComputed}
						selectedRowsSignal={selectedRowsSignal}
						canEditComputed={canEditComputed}
						handleSelectRow={handleSelectRow}
						showPushFormsModal={showPushFormsModal}
						renderDeviceCell={renderDeviceCell}
					/></tbody>
					<tfoot><tr><td colSpan={activeColumnsComputed.value.length + 2}>{
						displayDataComputed.value.length > 0 && (
							<>Displaying {startDisplayRowNumberComputed.value}-{endDisplayRowNumberComputed.value} of {filteredDataComputed.value.length}</>
						)
					}</td></tr></tfoot>
				</Table>
				<Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
				<DebugAccordion data={accordionData} debug={debugSignal} />

				<IncompatibleDevicesModal
					showSignal={showModalIncompatibleDevicesSignal}
					closeModal={handleCloseModalIncompatibleDevices}
					deviceIncompatibilitySignal={deviceIncompatibilitySignal}
					utgIncompatibilitySignal={utgIncompatibilitySignal}
					pciIncompatibilitySignal={pciIncompatibilitySignal}
				></IncompatibleDevicesModal>

				<PushFormsModal
					showSignal={showModalPushFormsSignal}
					handleCancel={handleCloseModalPushForms}
					taskType={taskTypeSignal.value}
					deviceFormsSignal={deviceFormsSignal}
					selectedDevices={selectedDevicesSignal.value}
					devicePciVersionSignal={devicePciVersionSignal}
					showDownloadProgressSignal={showDownloadProgressSignal}
					downloadProgressMessageSignal={downloadProgressMessageSignal}
				></PushFormsModal>

				<DeviceKeysModal
					show={showModalDeviceKeysSignal.value}
					handleClose={handleCloseModalDeviceKeys}
					selectedRecord={selectedRecordSignal.value}
				></DeviceKeysModal>

				<DevicesListFilteringModal
					show={showDevicesFilteringModalSignal.value}
					handleCancel={handleCancelDevicesFiltering}
					handleSubmit={handleApplyDevicesFilters}
				></DevicesListFilteringModal>
			</>
		)
	}
	catch (x) {
		console.trace(x.toString())
		window.alert(x.toString())
		throw x
	}
}

export default Devices
